import './App.css';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { UserProvider, UserContext } from './contexts/UserContext';

import DigitalCardSettings from './components/DigitalCard/DigitalCardSettings';
import DigitalCardGenerator from './components/DigitalCard/DigitalCardGenerator';
import DiscoverJsonFixer from './components/Discover/DiscoverJsonFixer';
import Shop from './components/Shop/Shop';
import ProfileSettings from './components/Profiles/ProfileSettings';

// Import WelcomePage
import WelcomePage from './components/Welcome/WelcomePage';

// Import Banner component
import Banner from './components/Banner/Banner';

// Import ALL admin components
import AdminNavigation from './components/Admin/AdminNavigation';
import ProductCuration from './components/Admin/ProductCuration';
import DiscoverCuration from './components/Admin/DiscoverCuration';
import EditorialCuration from './components/Admin/EditorialCuration';
import SignalsCuration from './components/Admin/SignalsCuration';
import UserContentCuration from './components/Admin/UserContentCuration';
import UserProductCuration from './components/Admin/UserProductCuration';
import AffiliateApproval from './components/Admin/AffiliateApproval';
import RevenueTracking from './components/Admin/RevenueTracking';
import RevenueAnalytics from './components/Admin/RevenueAnalytics';
import GuidesCuration from './components/Admin/GuidesCuration';

import Login from './components/Auth/Login';
import AccountCreation from './components/Auth/AccountCreation';
import EmailVerificationSuccess from './components/Auth/EmailVerificationSuccess';
import AuthRoutes from './components/Auth/AuthRoutes';
import Editorials from './components/Editorials/Editorials';
import EditorialDetail from './components/Editorials/EditorialDetail';
import DiscoverWithFilter from './components/Discover/DiscoverWithFilter';
import DiscoverDetail from './components/Discover/DiscoverDetail';
import Profiles from './components/Profiles/Profiles';
import Signals from './components/Signals/Signals';
import SignalDetail from './components/Signals/SignalDetail';
import UserProfile from './components/Profiles/UserProfile';
import UserProfileDetail from './components/Profiles/UserProfileDetail';
import EditProfile from './components/Profiles/EditProfile';
import UploadPortal from './components/Profiles/UploadPortal';
import UserSettings from './components/Profiles/UserSettings';
import Guides from './components/Guides/Guides';
import ResetPassword from './components/Auth/ResetPassword';
import ForgotPassword from './components/Auth/ForgotPassword';

// Import contributor components
import ContributorOnboard from './components/Contributor/ContributorOnboard';
import ContributorDashboard from './components/Contributor/ContributorDashboard';
import TrackingRedirect from './components/Contributor/TrackingRedirect';

// Import info pages
import About from './info-pages/About';
import Help from './info-pages/Help';
import Terms from './info-pages/Terms';
import Privacy from './info-pages/Privacy';

// Firebase Auth Action Handler - redirects to card generator
const FirebaseAuthActionHandler = () => {
  console.log('🔥 FirebaseAuthActionHandler called!');
  console.log('🔥 Current URL:', window.location.href);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    console.log('🔥 useEffect running');
    const mode = searchParams.get('mode');
    const email = searchParams.get('email') || '';
    console.log('🔥 Mode:', mode);

    if (mode === 'verifyEmail') {
      console.log('🔥 Redirecting to www domain card generator');
      // Force redirect to working www domain
      window.location.href = `https://www.a-r-t-i-f-e-x.com/card-generator?welcome=true&email=${encodeURIComponent(email)}`;
    } else {
      // For other modes, redirect to www domain home
      window.location.href = 'https://www.a-r-t-i-f-e-x.com/discover';
    }
  }, [searchParams]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '34vh',
      gap: '0.476rem'
    }}>
      <div style={{
        width: '13.9px',
        height: '13.9px',
        border: '1.4px solid #f3f3f3',
        borderTop: '1.4px solid #0d47a1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <p>Redirecting to Digital Card Generator...</p>
    </div>
  );
};

// Admin protection component using UserContext
const AdminRoute = ({ children }) => {
  const { user, isLoading } = useContext(UserContext);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '34vh',
        gap: '0.476rem'
      }}>
        <div style={{
          width: '20.4px',
          height: '20.4px',
          border: '2px solid #f3f3f3',
          borderTop: '2px solid #0d47a1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Checking admin access...</p>
      </div>
    );
  }

  if (!user || user.isGuest) {
    return <Navigate to="/login" />;
  }

  // Check for admin role OR specific admin email addresses
  const isAdminUser = user.userRole === 'admin' ||
                     user.email === 'matt.c.goff@gmail.com' ||
                     user.email === 'matt.c.goff@me.com';

  if (!isAdminUser) {
    return (
      <div style={{
        padding: '0.952rem',
        textAlign: 'center',
        maxWidth: '277.4px',
        margin: '0.952rem auto',
        background: '#ffebee',
        borderRadius: '3.7px',
        border: '1px solid #ffcdd2'
      }}>
        <h2>Access Denied</h2>
        <p>You need admin privileges to access this page.</p>
        <p><strong>Your email:</strong> {user.email}</p>
        <p><strong>Your role:</strong> {user.userRole}</p>
        <p><strong>Required:</strong> admin</p>
        <Link to="/discover" style={{ color: '#0066cc' }}>← Back to Discover</Link>
      </div>
    );
  }

  return children;
};

// Component wrapper for UploadPortal to handle standalone route
const StandaloneUploadPortal = () => {
  const [showUploader, setShowUploader] = useState(true);

  if (!showUploader) {
    return <Navigate to="/discover" />;
  }

  return <UploadPortal onClose={() => setShowUploader(false)} />;
};

// Main App Content Component
function AppContent() {
  const { user, firebaseUser, isLoading } = useContext(UserContext);
  const [showTopBar, setShowTopBar] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [showNavigationOverlay, setShowNavigationOverlay] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const lastScrollY = useRef(0);
  const location = useLocation();

  // Check if we're on the welcome page
  const isWelcomePage = location.pathname === '/';

  // Helper function for admin check
  const isAdmin = (user) => {
    return user && (user.userRole === 'admin' ||
                    user.email === 'matt.c.goff@gmail.com' ||
                    user.email === 'matt.c.goff@me.com');
  };

  // Hamburger and search overlay handlers
  const handleNavigationToggle = useCallback(() => {
    setShowNavigationOverlay(prev => !prev);
  }, []);

  const handleSearchToggle = useCallback(() => {
    setShowSearchOverlay(prev => !prev);
  }, []);

  // Click-away functionality for overlays
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNavigationOverlay && !event.target.closest('.app-navigation-overlay-content') && !event.target.closest('.hamburger-menu')) {
        setShowNavigationOverlay(false);
      }
      if (showSearchOverlay && !event.target.closest('.app-search-overlay-content') && !event.target.closest('.search-icon')) {
        setShowSearchOverlay(false);
      }
    };

    if (showNavigationOverlay || showSearchOverlay) {
      document.body.classList.add('navigation-overlay-open');
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    } else {
      document.body.classList.remove('navigation-overlay-open');
    }

    return () => {
      document.body.classList.remove('navigation-overlay-open');
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showNavigationOverlay, showSearchOverlay]);

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      const searchEvent = new CustomEvent('artifex-search', {
        detail: { searchTerm: searchValue.trim() }
      });
      window.dispatchEvent(searchEvent);
      console.log('Dispatched search event:', searchValue.trim());
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);

    if (e.target.value.trim()) {
      const searchEvent = new CustomEvent('artifex-search', {
        detail: { searchTerm: e.target.value.trim() }
      });
      window.dispatchEvent(searchEvent);
    } else {
      const searchEvent = new CustomEvent('artifex-search', {
        detail: { searchTerm: '' }
      });
      window.dispatchEvent(searchEvent);
    }
  };

  // Handle scroll-based top bar and navbar visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const navbar = document.querySelector('.main-navbar');

      if (currentScrollY > 100) {
        const isScrollingUp = currentScrollY < lastScrollY.current;
        setShowTopBar(isScrollingUp);

        // Handle navbar visibility when sticky
        if (navbar) {
          if (isScrollingUp) {
            navbar.classList.remove('sticky-hidden');
            navbar.classList.add('sticky-visible');
          } else {
            navbar.classList.remove('sticky-visible');
            navbar.classList.add('sticky-hidden');
          }
        }
      } else {
        setShowTopBar(true);
        // Always show navbar at top of page
        if (navbar) {
          navbar.classList.remove('sticky-hidden', 'sticky-visible');
        }
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Show loading screen while auth is initializing
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '68vh',
        gap: '0.68rem'
      }}>
        <div style={{
          width: '18.5px',
          height: '18.5px',
          border: '1.8px solid #f3f3f3',
          borderTop: '1.8px solid #0d47a1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Loading Artifex...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Navigation Overlay Modal */}
      {showNavigationOverlay && (
        <>
          <div className="overlay-backdrop" onClick={() => {
            setShowNavigationOverlay(false);
            document.body.classList.remove('navigation-overlay-open');
          }} />
          <div className="app-navigation-overlay">
            <div className="app-navigation-overlay-content">
            <Link
              to="/discover"
              onClick={() => {
                setShowNavigationOverlay(false);
                document.body.classList.remove('navigation-overlay-open');
              }}
            >
              DISCOVER
            </Link>
            <Link
              to="/guides"
              onClick={() => {
                setShowNavigationOverlay(false);
                document.body.classList.remove('navigation-overlay-open');
              }}
            >
              GUIDES
            </Link>
            <Link
              to="/editorials"
              onClick={() => {
                setShowNavigationOverlay(false);
                document.body.classList.remove('navigation-overlay-open');
              }}
            >
              EDITORIALS
            </Link>
            <Link
              to="/signals"
              onClick={() => {
                setShowNavigationOverlay(false);
                document.body.classList.remove('navigation-overlay-open');
              }}
            >
              SIGNALS
            </Link>
            <Link
              to="/shop"
              onClick={() => {
                setShowNavigationOverlay(false);
                document.body.classList.remove('navigation-overlay-open');
              }}
            >
              SHOP
            </Link>
          </div>
        </div>
        </>
      )}

      {/* Search Overlay Modal */}
      {showSearchOverlay && (
        <div className="app-navigation-overlay">
          <div className="app-search-overlay-content">
            <form onSubmit={(e) => {
              handleSearch(e);
              setShowSearchOverlay(false);
              document.body.classList.remove('navigation-overlay-open');
            }}>
              <input
                type="text"
                placeholder="Search Artifex..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
              />
              <button type="submit">SEARCH</button>
            </form>
          </div>
        </div>
      )}

      {/* Clean top navigation bar - HIDDEN on welcome page */}
      {!isWelcomePage && (
        <header className={`top-bar ${showTopBar ? 'visible' : 'hidden'}`}>
          {/* Left side - Logo */}
          <div className="top-bar-left">
            <Link to="/" className="artifex-logo">
              <div className="logo-container">
                <div className="logo-square">A</div>
                <span className="logo-text">A R T I F E X</span>
              </div>
            </Link>
          </div>

          {/* Center - Search */}
          <div className="search-container" style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: '171.2px',
            width: '171.2px'
          }}>
            <form className="search-form" onSubmit={handleSearch} style={{ display: 'flex', width: '100%', position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '0.7rem',
                color: '#999',
                pointerEvents: 'none',
                zIndex: 1
              }}>🔍</span>
              <input
                type="text"
                placeholder=""
                value={searchValue}
                onChange={handleSearchChange}
                className="search-input"
                style={{
                  width: '100%',
                  padding: '0.204rem 0.476rem 0.204rem 1.8rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '9.2px',
                  outline: 'none',
                  fontSize: '0.612rem',
                  backgroundColor: '#f5f5f5',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0d47a1';
                  e.target.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#ddd';
                  e.target.style.backgroundColor = '#f5f5f5';
                }}
              />
            </form>
          </div>

          {/* Right side - Settings and User Profile */}
          <div className="top-bar-right">
            {/* Hamburger menu - shown on mobile */}
            <button
              className="hamburger-menu"
              type="button"
              aria-label="Menu"
              onClick={handleNavigationToggle}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
              </svg>
            </button>

            {/* Search icon - shown on mobile */}
            <button
              className="search-icon"
              type="button"
              aria-label="Search"
              onClick={handleSearchToggle}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
              </svg>
            </button>

            {/* Admin access button - only show for admin */}
            {isAdmin(user) && (
              <Link to="/admin" className="icon-button admin-icon" title="Admin Dashboard">
                <svg xmlns="http://www.w3.org/2000/svg" width="13.6" height="13.6" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M6 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-5 6s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H1zM11 3.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5zm.5 2.5a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1h-4zm2 3a.5.5 0 0 0 0 1h2a.5.5 0 0 0 0-1h-2zm0 3a.5.5 0 0 0 0 1h2a.5.5 0 0 0 0-1h-2z"/>
                </svg>
              </Link>
            )}

            {/* Settings icon linking to settings page */}
            {user && !user.isGuest && (
              <Link to="/user-settings" className="icon-button settings-icon" title="Settings">
                <svg xmlns="http://www.w3.org/2000/svg" width="13.6" height="13.6" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
                  <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319z"/>
                </svg>
              </Link>
            )}

            {/* User profile icon linking to user's profile page */}
            {user && !user.isGuest ? (
              <Link
                to={`/profiles/${user.email?.split('@')[0] || user.uid}`}
                className="icon-button user-icon"
                title={user.displayName || 'Profile'}
              >
                <div className="user-profile-circle">
                  {firebaseUser?.photoURL ? (
                    <img src={firebaseUser.photoURL} alt="User" className="user-profile-image" />
                  ) : (
                    <div className="user-initial">{(user.displayName || user.email || 'U')[0].toUpperCase()}</div>
                  )}
                </div>
              </Link>
            ) : (
              <Link to="/login" className="icon-button login-button">
                Login
              </Link>
            )}
          </div>
        </header>
      )}

      {/* Banner component - HIDDEN on welcome page - page-specific styling */}
      {!isWelcomePage && <Banner />}

      {/* Main navigation menu */}
      {!isWelcomePage && (
        <nav className="navbar main-navbar">
          <div className="nav-links" style={{gap: '4rem'}}>
            <Link to="/discover" style={{fontSize: '.9rem'}}>DISCOVER</Link>
            <Link to="/guides" style={{fontSize: '.9rem'}}>GUIDES</Link>
            <Link to="/editorials" style={{fontSize: '.9rem'}}>EDITORIALS</Link>
            <Link to="/signals" style={{fontSize: '.9rem'}}>SIGNALS</Link>
            <Link to="/shop" style={{fontSize: '.9rem'}}>SHOP</Link>
          </div>
        </nav>
      )}

      {/* Application Routes */}
      <div className={isWelcomePage ? "welcome-content" : "content-wrapper"}>
        <Routes>
          {/* Firebase Auth Action Handler - redirects to card generator */}
          <Route path="/__/auth/action" element={<FirebaseAuthActionHandler />} />

          {/* WELCOME PAGE - NEW DEFAULT ROUTE */}
          <Route path="/" element={<WelcomePage />} />

          {/* Authentication routes - MOVED BEFORE FALLBACK */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<AccountCreation />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/email-verification-success" element={<EmailVerificationSuccess />} />
          <Route path="/verify-email" element={<Navigate to="/auth/verify-email" />} />
          <Route path="/auth/*" element={<AuthRoutes />} />

          {/* Main app routes */}
          <Route path="/shop" element={<Shop />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/editorials" element={<Editorials />} />
          <Route path="/editorials/:id" element={<EditorialDetail />} />
          <Route path="/discover" element={<DiscoverWithFilter />} />
          <Route path="/discover/:id" element={<DiscoverDetail />} />
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/profiles/:username" element={<UserProfile />} />
          <Route path="/profiles/:username/:itemId" element={<UserProfileDetail />} />
          <Route path="/edit-profile" element={
            user && user.emailVerified ? <EditProfile /> : <Navigate to="/login" />
          } />
          <Route path="/profile/settings" element={
            user && user.emailVerified ? <ProfileSettings /> : <Navigate to="/login" />
          } />
          <Route path="/signals" element={<Signals />} />
          <Route path="/signals/:slug" element={<SignalDetail />} />

          <Route path="/post" element={
            user && user.emailVerified ? <StandaloneUploadPortal /> : <Navigate to="/login" />
          } />

          {/* Settings routes */}
          <Route path="/user-settings" element={
            user && user.emailVerified ? <UserSettings /> : <Navigate to="/login" />
          } />

          {/* Digital Card routes */}
          <Route path="/settings/digital-card" element={
            user && user.emailVerified ? <DigitalCardSettings /> : <Navigate to="/login" />
          } />
          <Route path="/card-generator" element={<DigitalCardGenerator />} />

          {/* COMPLETE ADMIN ROUTES - All protected by admin role */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminNavigation />
            </AdminRoute>
          } />

          <Route path="/admin/products" element={
            <AdminRoute>
              <ProductCuration />
            </AdminRoute>
          } />

          <Route path="/admin/discover" element={
            <AdminRoute>
              <DiscoverCuration />
            </AdminRoute>
          } />

          <Route path="/admin/guides" element={
            <AdminRoute>
              <GuidesCuration />
            </AdminRoute>
          } />

          <Route path="/admin/revenue" element={
            <AdminRoute>
              <RevenueTracking />
            </AdminRoute>
          } />

          <Route path="/admin/analytics" element={
            <AdminRoute>
              <RevenueAnalytics />
            </AdminRoute>
          } />

          <Route path="/admin/editorial" element={
            <AdminRoute>
              <EditorialCuration />
            </AdminRoute>
          } />

          <Route path="/admin/signals" element={
            <AdminRoute>
              <SignalsCuration />
            </AdminRoute>
          } />

          <Route path="/admin/user-content" element={
            <AdminRoute>
              <UserContentCuration />
            </AdminRoute>
          } />

          <Route path="/admin/user-products" element={
            <AdminRoute>
              <UserProductCuration />
            </AdminRoute>
          } />

          <Route path="/admin/affiliates" element={
            <AdminRoute>
              <AffiliateApproval />
            </AdminRoute>
          } />

          {/* Contributor routes */}
          <Route path="/contributor/onboard" element={
            user && user.emailVerified ? <ContributorOnboard /> : <Navigate to="/login" />
          } />
          <Route path="/contributor/dashboard" element={
            user && user.emailVerified ? <ContributorDashboard /> : <Navigate to="/login" />
          } />
          <Route path="/track/:contributorId/:productId" element={<TrackingRedirect />} />

          {/* Info pages routes */}
          <Route path="/about" element={<About />} />
          <Route path="/help" element={<Help />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Original hidden routes still accessible if needed */}
          <Route path="/json-fixer" element={<DiscoverJsonFixer />} />

          {/* Default fallback route */}
          <Route path="*" element={<DiscoverWithFilter />} />
        </Routes>
      </div>

      <footer className="footer">
        <div style={{ marginBottom: '0.476rem', display: 'flex', justifyContent: 'center', gap: '0.68rem' }}>
          <Link to="/about" style={{ color: 'inherit', textDecoration: 'none' }}>About</Link>
          <Link to="/help" style={{ color: 'inherit', textDecoration: 'none' }}>Help</Link>
          <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</Link>
          <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</Link>
        </div>
        <div>© Mirador Pacific 2025</div>
      </footer>
    </div>
  );
}

// Main App wrapper with UserProvider
function App() {
  return (
    <Router>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </Router>
  );
}

export default App;
